import React, { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, NotYetImplemented } from '../../components/ui';
import { instantiateTemplate } from '../../api/templatesApi';
import { billingErrorMessage } from '../../utils/billingErrors';
import { monthNameUTC } from '../../utils/dates';
import { useAuth } from '../../context/useAuth';
import { useTask } from './useTask';
import StepGraph from './StepGraph';
import InputField from './InputField';
import { copyToCanvas } from './canvasCopy';
import { parallelSummary } from './taskGraph';
import { missingRequired, readLastInputs, toInputValues, writeLastInputs } from './runInputs';

const LINK = 'underline text-ink font-semibold focus-ring';

function initialState(userId, id, inputs) {
  const last = readLastInputs(userId, id);
  const names = new Set(inputs.map((i) => i.name));
  const values = {};
  const carried = {};
  if (last) {
    Object.keys(last.values).forEach((k) => {
      if (!names.has(k)) return;
      values[k] = last.values[k];
      if (last.at) carried[k] = monthNameUTC(last.at);
    });
  }
  return { values, carried };
}

function RunTask({ id, task }) {
  const { template, graph, levels, inputs } = task;
  const navigate = useNavigate();
  const userId = useAuth()?.user?.id;
  const title = template.template_name;
  const [init] = useState(() => initialState(userId, id, inputs));
  const [values, setValues] = useState(init.values);
  const [carried, setCarried] = useState(init.carried);
  const [invalid, setInvalid] = useState([]);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null); // { message, topUp }
  const [copyError, setCopyError] = useState(false);
  const copying = useRef(false);

  const change = (name, v) => {
    setValues((s) => ({ ...s, [name]: v }));
    setCarried((s) => { const { [name]: _drop, ...rest } = s; return rest; });
    setInvalid((s) => s.filter((n) => n !== name));
  };

  const noSteps = graph.steps.length === 0;
  const blocked = running || noSteps || missingRequired(inputs, values).length > 0;

  const run = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const res = await instantiateTemplate(id, toInputValues(inputs, values), {
        taskDescription: template.description || template.template_name,
        domain: template.domain || undefined,
        role: template.role || undefined,
        execute: true,
      });
      writeLastInputs(userId, id, values);
      navigate(`/runs/open?workflow=${encodeURIComponent(res.workflow_id)}&view=visualization`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail?.error === 'required_inputs_missing') {
        setInvalid(Array.isArray(detail.missing) ? detail.missing : []);
      } else if (status === 402) {
        setRunError({ message: billingErrorMessage(err, 'Not enough credits to run this task.'), topUp: true });
      } else {
        setRunError({ message: "Couldn't start the run.", topUp: false });
      }
      setRunning(false);
    }
  };

  const copy = async () => {
    if (copying.current) return;
    copying.current = true;
    setCopyError(false);
    try {
      const flowId = await copyToCanvas({ name: title, graph, source: { kind: 'task', templateId: id } });
      navigate(`/canvas?flow=${encodeURIComponent(flowId)}`);
    } catch (e) {
      setCopyError(true);
    } finally {
      copying.current = false;
    }
  };

  const lead = (
    <Card variant="static" className="p-3 border-dashed">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-sans font-bold text-ink">Get the inputs ready</span>
        <span className="bg-eraser text-ink text-xs font-bold uppercase tracking-wider rounded px-1.5 py-0.5">THIS RUN</span>
      </div>
    </Card>
  );

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-6xl mx-auto px-6 py-9 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <nav aria-label="Breadcrumb" className="text-sm text-pencil flex gap-1.5">
            <Link to="/tasks" className={LINK}>Tasks</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/tasks/${encodeURIComponent(id)}`} className={LINK}>{title}</Link>
          </nav>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{`Run ${title}`}</h1>
          {template.description ? <p className="text-pencil">{template.description}</p> : null}
        </header>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0 flex flex-col gap-8 w-full">
            {inputs.length > 0 && (
              <section aria-label="Inputs" className="flex flex-col gap-5">
                {inputs.map((input) => (
                  <InputField
                    key={input.name}
                    input={input}
                    value={values[input.name] ?? null}
                    carriedFrom={carried[input.name]}
                    invalid={invalid.includes(input.name)}
                    onChange={(v) => change(input.name, v)}
                  />
                ))}
              </section>
            )}

            <section>
              <NotYetImplemented feature="taskRuns">
                <h2 className="font-display text-2xl font-extrabold">Previous runs</h2>
              </NotYetImplemented>
            </section>
          </div>

          <aside aria-label="What will happen" className="w-full lg:w-[360px] lg:shrink-0 flex flex-col gap-4">
            <h2 className="font-display text-2xl font-extrabold">What will happen</h2>
            <NotYetImplemented feature="versions">
              <select aria-label="Version" disabled className="w-full border-1.5 border-ink rounded-[10px] bg-card text-ink text-sm px-3 py-2">
                <option>Latest</option>
              </select>
            </NotYetImplemented>
            <p className="text-sm font-semibold">{parallelSummary(graph, levels)}</p>
            <StepGraph graph={graph} lead={noSteps ? undefined : lead} compact />
            <NotYetImplemented feature="runEstimate">
              <div className="text-sm text-pencil">Estimate for these inputs</div>
            </NotYetImplemented>
            <Button variant="primary" onClick={run} disabled={blocked}>Run now</Button>
            {runError && (
              <div role="alert" className="text-sm text-status-failed flex items-center gap-3 flex-wrap">
                <span>{runError.message}</span>
                {runError.topUp && <Link to="/billing" className={LINK}>Top up</Link>}
              </div>
            )}
            <Button variant="secondary" onClick={copy}>Copy to canvas</Button>
            {copyError && <div role="alert" className="text-sm text-status-failed">Couldn't copy to the canvas.</div>}
          </aside>
        </div>
      </div>
    </div>
  );
}

export default function RunTaskPage() {
  const { id } = useParams();
  const task = useTask(id);
  if (task.loading) return <div className="p-9 text-pencil">Loading…</div>;
  if (task.error || !task.template) {
    return (
      <div className="p-9 flex items-center gap-3">
        <p role="alert" className="text-status-failed">Couldn't open this task.</p>
        <Link to="/tasks" className={LINK}>Tasks</Link>
      </div>
    );
  }
  return <RunTask key={id} id={id} task={task} />;
}
